import { useCallback, useEffect, useState } from 'react';
import { HEADER_HEIGHT } from 'components/Header/Header.styled';

interface StickyAccordionInfo {
  id: string;
  isExpanded: boolean;
  ref: React.RefObject<HTMLDivElement>;
}

const VIEWPORT_OFFSET = -150;

export const useStickyAccordionManager = () => {
  const [stickyAccordions, setStickyAccordions] = useState<
    StickyAccordionInfo[]
  >([]);
  const [activeStickyId, setActiveStickyId] = useState<string | null>(null);

  const registerAccordion = useCallback(
    (id: string, isExpanded: boolean, ref: React.RefObject<HTMLDivElement>) => {
      setStickyAccordions((prev: StickyAccordionInfo[]) => {
        const existing = prev.find((item) => item.id === id);
        if (existing) {
          // Only update if values actually changed
          if (existing.isExpanded === isExpanded && existing.ref === ref) {
            return prev;
          }
          return prev.map((item) =>
            item.id === id ? { ...item, isExpanded, ref } : item,
          );
        }
        return [...prev, { id, isExpanded, ref }];
      });
    },
    [],
  );

  const unregisterAccordion = useCallback((id: string) => {
    setStickyAccordions((prev: StickyAccordionInfo[]) =>
      prev.filter((item) => item.id !== id),
    );
  }, []);

  useEffect(() => {
    const currentAccordions: StickyAccordionInfo[] = stickyAccordions;
    const handleScroll = () => {
      const expandedAccordions = currentAccordions.filter(
        (item) => item.isExpanded,
      );

      if (expandedAccordions.length === 0) {
        setActiveStickyId(null);
        return;
      }

      let closestAccordion: StickyAccordionInfo | null = null;
      let minDistance = Infinity;

      expandedAccordions.forEach((accordion) => {
        if (!accordion.ref.current) return;

        const rect = accordion.ref.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom < -1 * VIEWPORT_OFFSET) {
          return;
        }

        if (rect.top > viewportHeight) {
          return;
        }

        const distance = Math.abs(rect.top - HEADER_HEIGHT);

        if (rect.top <= HEADER_HEIGHT && distance < minDistance) {
          minDistance = distance;
          closestAccordion = accordion;
        }
      });

      if (closestAccordion) {
        setActiveStickyId((closestAccordion as StickyAccordionInfo).id);
      } else {
        setActiveStickyId(null);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [stickyAccordions]);

  return {
    registerAccordion,
    unregisterAccordion,
    activeStickyId,
  };
};
